import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { views } from '../../../constants/savedAgents';
import Loader from '@lwt-helix/loader';
import { NoSavedAgentsMessage } from './common/NoSavedAgentsMessage';
import { agentProductionTerms as terms } from '../../../../constants';
import { NoNewActivitiesMessage } from './common/NoNewActivitiesMessage';
import Table from '../../../../common/components/table/agGrid/Table';
import { useSavedAgentsActivities } from '../../../hooks/savedAgentsActivities';
import { useSearchStore } from '../../../../store/store';
import { useSearchResultsStore } from '../../../../store/searchResults/store';
import { useSavedAgentsStore, useCommonStore } from '../../../../store/store';
import AgentsFloatingActionBar from '../../common/AgentsFloatingActionBar';
import { useFloatingActionBar } from '../../../hooks/floatingActionBar';
import { exportCsv } from '../../common/exportCsv';

export const SavedAgentsContent = ({
    savedAgentState,
    gridRef,
    tableSettingsByView,
    setState,
}) => {
    const { currentModule } = useSearchStore((state) => ({
        currentModule: state.selectedModule
    }));
    const { searchResults } = useSearchResultsStore((state) => ({
        searchResults: state[currentModule]?.searchResults
    }));
    const { 
        selectedView,
      } = useSavedAgentsStore(state => ({
        selectedView: state.selectedView
    }));
    const { crossSortOrder, sortByStoredOrder } = useSearchStore(state => ({
        crossSortOrder: state[state.selectedModule]?.crossSortOrder,
        sortByStoredOrder: state.sortByStoredOrder
    }));
    const { 
        timeIntervals,
      } = useCommonStore(state => ({
        timeIntervals: state.timeIntervals
    }));

    const handleDownload = (selectedAgents) => {
        exportCsv(
            {
                ...savedAgentState,
                timeIntervals,
            },
            setState,
            selectedView.value,
            selectedAgents,
            selectedView.value
        );
    };

    const {
        selectedCount,
        showAddToListModal,
        setShowAddToListModal,
        handleDismiss,
        handleDownloadClick,
    } = useFloatingActionBar(gridRef, handleDownload);

    const { activityRowData } = savedAgentState;
    const { summaryPinnedConfig, shouldShowPinnedRow, summaryPinnedData, isPageLoading, activities, agentLists } =
    useSavedAgentsActivities({ savedAgentState });
    
    const sortedRowData = useMemo(() => {
        const data = Array.isArray(searchResults) ? searchResults : searchResults?.results;
        return sortByStoredOrder(data) ?? [];
    }, [crossSortOrder, searchResults]);

    const adjustedRowData = (
        selectedView.value === views.activity ? activityRowData : sortedRowData
    )?.map((d) => ({ ...d, hasFavStar: true }));
    
    if (agentLists !== undefined && !agentLists.length)
        return (
            <div className="mt-5">
                <NoSavedAgentsMessage isDropdown={false} />
            </div>
        );
 
    if (isPageLoading || agentLists === undefined) return (<div className='py-3'><Loader /></div>);

    if (selectedView.value === views.activity && activities?.length === 0 && searchResults?.length > 0) {
        return (
            <NoNewActivitiesMessage />
        );
    }

    const hasResults = !!searchResults?.length;

    if (!hasResults) return (
        <div className='py-5 d-flex justify-content-center'>
            <p className='no-search-results-suggestion'>{terms.viewOnlyMyAgentsNoData}</p>
        </div>);

    return (
        <div className='px-2 mb-5'>
            <Table
                additionalTableHeaders={tableSettingsByView[selectedView.value]?.additionalTableHeaders}
                columns={tableSettingsByView[selectedView.value]?.columns}
                gridRef={gridRef}
                rowData={adjustedRowData}
                tableTitleWithTooltips={tableSettingsByView[selectedView.value]?.tableTitleWithTooltips}
                tableView={tableSettingsByView[selectedView.value]?.tableView}
                pinnedBottomRowData={shouldShowPinnedRow && [{...summaryPinnedData}]}
                externalParams={shouldShowPinnedRow && {summaryPinnedConfig}}
                hasFooter={selectedView.value !== views.activity}
                rowSelection={'multiple'}
                suppressRowClickSelection={true}
                rowMultiSelectWithClick={true}
            />
            <AgentsFloatingActionBar
                selectedCount={selectedCount}
                showAddToListModal={showAddToListModal}
                setShowAddToListModal={setShowAddToListModal}
                onDismiss={handleDismiss}
                onDownloadClick={handleDownloadClick}
            />
        </div>
    );
};

SavedAgentsContent.propTypes = {
    savedAgentState: PropTypes.object,
    gridRef: PropTypes.object,
    tableSettingsByView: PropTypes.object,
    setState: PropTypes.func,
};
